/**
 * Node identity — the glTF 2.1 `uid`.
 *
 * glTF 2.1 gives every child-of-root object an optional `uid`: a string from a
 * restricted **alphanumeric** character set, unique across every `uid` *and*
 * every `name` in the file, with a qualifying `name` serving as the fallback
 * identifier for 2.0 files and un-annotated 2.1 ones (Khronos
 * [glTF 2.1 announcement](https://www.khronos.org/blog/introducing-gltf-2.1-with-complex-scenes),
 * [KhronosGroup/glTF#2597](https://github.com/KhronosGroup/glTF/issues/2597);
 * `docs-internal/GLTF_2_1_MIGRATION_PLAN.md` D7). We adopt it as-is —
 * `node.uid`, not an `extras.trace` field — and mint ours as 22 characters of
 * base62 over 128 random bits.
 *
 * Two properties come out of that shape and both are load-bearing:
 *
 * - base62 is alphanumeric, so a minted uid always satisfies the 2.1 character
 *   set without escaping;
 * - 128 bits of randomness never collides with a `name` a person would type,
 *   so the "unique across uid and name" rule holds by construction rather than
 *   by a rename check.
 *
 * Randomness comes from the Web Crypto API (`crypto.getRandomValues`), which
 * the browser, the worker and the test environment all have — deliberately no
 * Node-only API, because this module is part of the runtime bundle.
 *
 * See `docs-internal/SCENE_ROOT_SPEC.md` §2.3 "Identity: glTF 2.1 `uid` — now"
 * (landing with tv#839) for the rules around this module: who mints, what a
 * round trip preserves, and why nothing resolves by a uid yet.
 */

/** The alphabet of base62, in ascending digit order. */
const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/** How many random bytes back one uid: 128 bits. */
const UID_BYTES = 16;

/**
 * Length of a minted uid: 22 characters.
 *
 * `62^22 > 2^128 > 62^21`, so 22 is the smallest base62 width that can carry
 * 128 bits — and every minted uid is padded to it, so they read as one kind of
 * thing rather than occasionally one character shorter.
 */
export const UID_LENGTH = 22;

/**
 * Whether a string is a legal glTF 2.1 identifier — the character-set half of
 * the rule (alphanumeric, non-empty).
 *
 * The uniqueness half is a property of a whole document, not of a string, and
 * is checked where documents are written (`tracex-exporter`) and where a
 * `name` is read as a fallback identifier (`gltf21/normalize`).
 */
export function isValidUid(value: string): boolean {
    return /^[A-Za-z0-9]+$/.test(value);
}

/** A fresh node identity: 22 characters of base62 over 128 random bits. */
export function mintUid(): string {
    const bytes = new Uint8Array(UID_BYTES);
    const source = globalThis.crypto;
    if (!source?.getRandomValues) {
        throw new Error('[uid] crypto.getRandomValues is unavailable — cannot mint a node uid');
    }
    source.getRandomValues(bytes);
    return toBase62(bytes).padStart(UID_LENGTH, '0');
}

/**
 * Base62 of a big-endian byte string, by repeated division.
 *
 * Long division rather than BigInt: the same arithmetic everywhere, no
 * dependency on a lowered target, and the numbers stay well inside the safe
 * integer range (`61 * 256 + 255 < 2^31`).
 */
function toBase62(bytes: Uint8Array): string {
    const work = Array.from(bytes);
    const digits: number[] = [];
    let start = 0;
    while (start < work.length) {
        let remainder = 0;
        for (let i = start; i < work.length; i++) {
            const accumulator = remainder * 256 + work[i];
            work[i] = Math.floor(accumulator / 62);
            remainder = accumulator % 62;
        }
        digits.push(remainder);
        while (start < work.length && work[start] === 0) start++;
    }
    if (digits.length === 0) return ALPHABET[0];
    let out = '';
    for (let i = digits.length - 1; i >= 0; i--) out += ALPHABET[digits[i]];
    return out;
}
